import React, { useState, useRef, useEffect } from 'react';
import '../styles/CustomSelect.css';

/**
 * A custom select component that doesn't rely on Material UI
 */
const CustomSelect = ({ 
  label,
  options,
  value,
  onChange,
  name,
  placeholder = 'Select an option',
  disabled = false,
  style = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  
  // Find the selected option label directly using the value prop
  const selectedOption = options.find(option => option.value === value);
  
  // Determine what text to display in the select box
  const displayText = selectedOption 
    ? selectedOption.label 
    : value && value.includes('@') 
      ? 'Select a user' // Don't show email addresses
      : placeholder;
  
  // DEBUGGING - Log key information
  console.log(`CustomSelect [${name}] render:`, {
    value,
    placeholder,
    displayText,
    selectedOption,
    optionsCount: options.length,
    firstFewOptions: options.slice(0, 3)
  });
  
  // Log some debugging info for difficult selects
  useEffect(() => {
    if (value && !selectedOption && options.length > 0) {
      console.log(`CustomSelect - Warning: No matching option found for value "${value}" in ${name}`);
      console.log(`Available options for ${name}:`, options);
    }
  }, [value, selectedOption, options, name]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  
  // Handle option selection
  const handleSelect = (option) => {
    // Create a synthetic event
    const syntheticEvent = {
      target: {
        name: name,
        value: option.value
      }
    };
    
    // Pass the event to parent
    if (onChange) {
      onChange(syntheticEvent);
    }
    
    setIsOpen(false);
  };
  
  return (
    <div className="custom-select-container" ref={selectRef}>
      {label && <label className="custom-select-label">{label}</label>}
      
      <div 
        className={`custom-select-button ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={toggleDropdown}
        style={style}
      >
        <span className="custom-select-value">
          {displayText}
          {!selectedOption && value && !value.includes('@') && <span style={{color: 'red'}}> (ID: {value})</span>}
        </span>
        <span className="custom-select-arrow">▼</span>
      </div>
      
      {isOpen && !disabled && (
        <div className="custom-select-dropdown">
          {options.length > 0 ? 
            options.map((option) => (
              <div
                key={option.value}
                className={`custom-select-option ${option.value === value ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            )) : 
            <div className="custom-select-option disabled">Loading options...</div>
          }
        </div>
      )}
    </div>
  );
};

export default CustomSelect; 